import {
    type AnnotationsMap,
    makeObservable,
    observable,
    action,
    computed,
    reaction,
    type IReactionDisposer
} from "mobx";
import axios from "axios";
import type { RootStore } from "./RootStore";
import { initI18n } from "@/app/initI18n";
import { AppLogger, ConfigurationError } from "@utils/index";
import DefaultConfig from "@/config.json";
import lastCommit from "@/last-commit";

/**
 * Application configuration interface
 */
export interface AppConfig {
  readonly api_uri: string;
}

/**
 * Application loading state
 */
export type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * Main application store with proper MobX patterns
 * - Uses dependency injection via RootStore
 * - Implements computed values for derived state
 * - Uses reactions for side effects
 * - Follows TypeScript best practices with interfaces
 */
export default class AppStore {
    // Observable state
    public loadingState: LoadingState = 'idle';
    public errorMessage: string | null = null;
    private _popupDisplayed = false;
    protected _config: AppConfig = DefaultConfig;

    // Reaction disposers for cleanup
    private readonly _disposers: IReactionDisposer[] = [];

    // Reference to root store for dependency injection
    private readonly rootStore: RootStore;
    private _startPromise: Promise<void> | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this, {
            loadingState: observable,
            errorMessage: observable,
            _popupDisplayed: observable,
            _config: observable,
            // Computed values
            loaded: computed,
            isPopUpDisplayed: computed,
            isInitialized: computed,
            isLoading: computed,
            hasError: computed,
            config: computed,
            // Actions
            setLoadingState: action,
            setError: action,
            clearError: action,
            showPopup: action,
            hidePopup: action,
            setConfig: action,
        } as AnnotationsMap<this, string>);

        this.setupReactions();
    }

    /**
   * Setup MobX reactions for side effects
   * Following mobx-reaction-usage rule
   */
    private setupReactions(): void {
    // React to loading state changes
        this._disposers.push(
            reaction(
                () => this.loadingState,
                (state, previousState) => {
                    AppLogger.debug(`Loading state changed: ${previousState} → ${state}`);
                    if (state === 'loaded') {
                        AppLogger.info("Application fully loaded and ready");
                    } else if (state === 'error') {
                        AppLogger.error("Application failed to load:", this.errorMessage);
                    }
                }
            )
        );

        // React to popup state changes
        this._disposers.push(
            reaction(
                () => this._popupDisplayed,
                (isDisplayed) => {
                    if (isDisplayed) {
                        AppLogger.debug("Popup displayed");
                        // Prevent body scroll when popup is open
                        document.body.style.overflow = 'hidden';
                    } else {
                        document.body.style.overflow = '';
                    }
                }
            )
        );
    }

    /**
   * Initialize the application
   */
    public start = async (): Promise<void> => {
        if (this._startPromise !== null) {
            return this._startPromise;
        }

        if (this.loadingState === 'loaded') {
            return;
        }

        this._startPromise = (async () => {
            this.setLoadingState('loading');

            try {
                await initI18n();
                AppLogger.info(`App Store started commit:${lastCommit.substring(0, 8)}`);

                // Load configuration
                this.setConfig(await this.loadConfig());
                AppLogger.debug("App Store loaded with config", this._config);

                const healthResult = await this.rootStore.apiClient.fetchHealth();
                if (!healthResult.success) {
                    AppLogger.warn("API health check failed, app will continue:", healthResult.error.message);
                }

                // Mark as loaded
                this.setLoadingState('loaded');
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Unknown error occurred';
                this.setError(message);
                this.setLoadingState('error');
                throw error;
            } finally {
                this._startPromise = null;
            }
        })();

        return this._startPromise;
    };

    // ============ Actions ============

    public setLoadingState(state: LoadingState): void {
        this.loadingState = state;
    }

    public setError(message: string): void {
        this.errorMessage = message;
    }

    public clearError(): void {
        this.errorMessage = null;
    }

    public showPopup(): void {
        this._popupDisplayed = true;
    }

    public hidePopup(): void {
        this._popupDisplayed = false;
    }

    public setConfig(config: AppConfig): void {
        this._config = config;
    }

    // ============ Computed Values ============

    /**
   * Legacy computed for backward compatibility
   */
    public get loaded(): boolean {
        return this.loadingState === 'loaded';
    }

    public get isPopUpDisplayed(): boolean {
        return this._popupDisplayed;
    }

    /**
   * Check if app is fully initialized with valid config
   */
    public get isInitialized(): boolean {
        return this.loaded && this._config !== null;
    }

    /**
   * Check if app is currently loading
   */
    public get isLoading(): boolean {
        return this.loadingState === 'loading';
    }

    /**
   * Check if there's an error
   */
    public get hasError(): boolean {
        return this.loadingState === 'error' || this.errorMessage !== null;
    }

    /**
   * Get the current configuration (readonly)
   */
    public get config(): Readonly<AppConfig> {
        return this._config;
    }

    /**
   * Get the current environment (not a MobX computed — reads no observables)
   */
    public get environment(): string {
        return import.meta.env.VITE_ENV ?? 'development';
    }

    // ============ Protected Methods ============

    protected async loadConfig(): Promise<AppConfig> {
        try {
            const url = `${window.location.origin}/config.json`;
            const response = await axios.get<AppConfig>(url);
            if (response.status === 200) {
                AppLogger.info(`Loaded config from url: ${url}`);
                return response.data;
            }
            throw new ConfigurationError('Failed to load config: invalid response');
        } catch (e) {
            AppLogger.warn('Failed to load config from url, using default', e);
            return this._config;
        }
    }

    /**
   * Cleanup reactions when store is disposed
   */
    public dispose(): void {
        this._disposers.forEach(disposer => disposer());
        this._disposers.length = 0;
        // Restore body overflow in case popup was open when disposed
        document.body.style.overflow = '';
        AppLogger.debug("AppStore disposed");
    }
}

